"use client";

import React from "react";
import type { GoogleConsentPreferences } from "@/lib/googleTracking";
import {
  GOOGLE_CONSENT_OPEN_EVENT,
  GOOGLE_CONSENT_TRIGGER_SELECTOR,
  registerGoogleConsentControls,
  type GoogleConsentOpenDetail,
} from "@/lib/googleConsentUiBridge";

type GoogleConsentControlsProps = {
  preferences: GoogleConsentPreferences;
  needsDecision: boolean;
  onCommit: (preferences: GoogleConsentPreferences) => void;
};

const ACCEPT_ALL: GoogleConsentPreferences = {
  analytics: true,
  advertising: true,
};

const REJECT_ALL: GoogleConsentPreferences = {
  analytics: false,
  advertising: false,
};

export default function GoogleConsentControls({
  preferences,
  needsDecision,
  onCommit,
}: GoogleConsentControlsProps) {
  const [isOpen, setIsOpen] = React.useState(needsDecision);
  const [isCustomizing, setIsCustomizing] = React.useState(false);
  const [draft, setDraft] =
    React.useState<GoogleConsentPreferences>(preferences);
  const panelRef = React.useRef<HTMLElement>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);

  const restoreFocus = React.useCallback(() => {
    const returnFocus = returnFocusRef.current;
    returnFocusRef.current = null;
    if (!returnFocus?.isConnected) return;
    window.requestAnimationFrame(() => returnFocus.focus());
  }, []);

  const openFrom = React.useCallback((returnFocus?: HTMLElement | null) => {
    returnFocusRef.current = returnFocus ?? null;
    setIsCustomizing(false);
    setIsOpen(true);
  }, []);

  React.useEffect(() => {
    const unregister = registerGoogleConsentControls();

    const onOpenRequest = (event: Event) => {
      const consentEvent = event as CustomEvent<GoogleConsentOpenDetail>;
      event.preventDefault();
      openFrom(consentEvent.detail?.returnFocus);
    };

    const onTriggerClick = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const trigger =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>(GOOGLE_CONSENT_TRIGGER_SELECTOR)
          : null;
      if (!trigger) return;

      event.preventDefault();
      openFrom(trigger);
    };

    window.addEventListener(GOOGLE_CONSENT_OPEN_EVENT, onOpenRequest);
    window.addEventListener("click", onTriggerClick, true);
    return () => {
      unregister();
      window.removeEventListener(GOOGLE_CONSENT_OPEN_EVENT, onOpenRequest);
      window.removeEventListener("click", onTriggerClick, true);
    };
  }, [openFrom]);

  React.useEffect(() => {
    if (!needsDecision) return;
    returnFocusRef.current = null;
    setIsOpen(true);
  }, [needsDecision]);

  React.useEffect(() => {
    if (!isOpen) return;
    setDraft(preferences);
    const frame = window.requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLElement>("[data-consent-autofocus]")
        ?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isCustomizing, isOpen, preferences]);

  React.useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || needsDecision) return;
      setIsOpen(false);
      setIsCustomizing(false);
      restoreFocus();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, needsDecision, restoreFocus]);

  const commit = (next: GoogleConsentPreferences) => {
    onCommit(next);
    setDraft(next);
    setIsCustomizing(false);
    setIsOpen(false);
    restoreFocus();
  };

  const close = () => {
    if (needsDecision) return;
    setIsOpen(false);
    setIsCustomizing(false);
    restoreFocus();
  };

  if (!isOpen) return null;

  return (
    <section
      ref={panelRef}
      className="openings-google-consent"
      role="dialog"
      aria-labelledby="openings-google-consent-title"
      aria-describedby="openings-google-consent-description"
    >
      <div className="openings-google-consent__heading">
        <div>
          <h2 id="openings-google-consent-title">Your privacy choices</h2>
          <p id="openings-google-consent-description">
            This site uses optional Google analytics and advertising cookies.
            You can accept all, reject all, or choose which categories to allow.
          </p>
        </div>
        {!needsDecision && (
          <button
            type="button"
            className="openings-google-consent__close"
            aria-label="Close privacy choices"
            onClick={close}
          >
            ×
          </button>
        )}
      </div>

      {isCustomizing ? (
        <>
          <fieldset className="openings-google-consent__categories">
            <legend>Optional cookie categories</legend>
            <label className="openings-google-consent__category">
              <input
                data-consent-autofocus
                type="checkbox"
                checked={draft.analytics}
                aria-describedby="openings-analytics-cookie-description"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    analytics: event.target.checked,
                  }))
                }
              />
              <span>
                <strong>Analytics</strong>
                <small id="openings-analytics-cookie-description">
                  Helps understand visits and improve the booking experience.
                </small>
              </span>
            </label>
            <label className="openings-google-consent__category">
              <input
                type="checkbox"
                checked={draft.advertising}
                aria-describedby="openings-advertising-cookie-description"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    advertising: event.target.checked,
                  }))
                }
              />
              <span>
                <strong>Advertising</strong>
                <small id="openings-advertising-cookie-description">
                  Lets this site measure bookings from Google ads. It does not
                  enable personalized advertising.
                </small>
              </span>
            </label>
          </fieldset>
          <div className="openings-google-consent__actions">
            <button
              type="button"
              className="openings-google-consent__button openings-google-consent__button--primary"
              onClick={() => commit(draft)}
            >
              Save choices
            </button>
            <button
              type="button"
              className="openings-google-consent__button"
              onClick={() => commit(REJECT_ALL)}
            >
              Reject all
            </button>
            <button
              type="button"
              className="openings-google-consent__button"
              onClick={() => commit(ACCEPT_ALL)}
            >
              Accept all
            </button>
          </div>
        </>
      ) : (
        <div className="openings-google-consent__actions">
          <button
            data-consent-autofocus
            type="button"
            className="openings-google-consent__button openings-google-consent__button--primary"
            onClick={() => commit(ACCEPT_ALL)}
          >
            Accept all
          </button>
          <button
            type="button"
            className="openings-google-consent__button"
            onClick={() => commit(REJECT_ALL)}
          >
            Reject all
          </button>
          <button
            type="button"
            className="openings-google-consent__button"
            onClick={() => setIsCustomizing(true)}
          >
            Customize
          </button>
        </div>
      )}
    </section>
  );
}
